import { useState } from "react";
import { ActionIcon, Group } from "@mantine/core";
import IconX from "@tabler/icons-react/dist/esm/icons/IconX.mjs";
import { useDroppable } from "@dnd-kit/core";

import LeftField from "./ConditionFields/LeftField";
import RightField from "./ConditionFields/RightField";
import ChangeOperator from "../ChangeOperator/ChangeOperator";
import { NestedPlaceholder } from "../Placeholder/Placeholder";
import { useDeleteConditionGroupMutation } from "../../hooks/mutations";
import { DeleteConfirmModal } from "../../../DeleteConfirmationModal";
import {
  DROP_CONDITION_SELECT,
  conditionSelectId,
} from "../../dnd/kinds";
import classes from "./Condition.module.scss";

/**
 * A single condition row: [left field] [operator] [right field] [delete].
 *
 * Drop targets:
 *   - LeftField / RightField: replace their side (own useDroppable inside)
 *   - The operator wrapper here: creates a NESTED condition group under
 *     this row (the only way to add nested groups)
 */
export default function Condition({
  ruleId,
  queryId,
  groupId,
  forJoin,
  fieldsData,
  withNestedPlaceholder = false,
  canEdit = true,
}) {
  const [deleteOpened, setDeleteOpened] = useState(false);
  const { mutate: deleteConditionGroup } = useDeleteConditionGroupMutation({
    ruleId,
  });

  const selectDropId = conditionSelectId({
    queryId,
    groupId,
    fieldId: fieldsData?.id,
  });
  const { setNodeRef: setSelectRef } = useDroppable({
    id: selectDropId,
    data: {
      kind: DROP_CONDITION_SELECT,
      fieldsData,
      queryId,
      groupId,
      forJoin,
    },
  });

  if (!fieldsData) return null;

  return (
    <div>
      <Group
        gap={8}
        align="center"
        wrap="nowrap"
        className={classes.conditionRow}
      >
        <LeftField
          ruleId={ruleId}
          queryId={queryId}
          groupId={groupId}
          forJoin={forJoin}
          fieldsData={fieldsData}
          canEdit={canEdit}
        />
        <div ref={setSelectRef}>
          <ChangeOperator
            ruleId={ruleId}
            fieldsData={fieldsData}
            canEdit={canEdit}
          />
        </div>
        <RightField
          ruleId={ruleId}
          queryId={queryId}
          groupId={groupId}
          forJoin={forJoin}
          fieldsData={fieldsData}
          canEdit={canEdit}
        />
        {canEdit && (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            aria-label="Remove condition"
            onClick={() => setDeleteOpened(true)}
          >
            <IconX size={16} />
          </ActionIcon>
        )}
      </Group>
      {withNestedPlaceholder && <NestedPlaceholder />}

      <DeleteConfirmModal
        opened={deleteOpened}
        onClose={() => setDeleteOpened(false)}
        onDelete={() => deleteConditionGroup({ id: fieldsData.id })}
        label="Delete condition?"
      />
    </div>
  );
}
